/**
 * audio.ts — Web Audio synthesis for the racer engine drone.
 *
 * No audio files. The engine sound is built from oscillators at runtime:
 *   • A sawtooth base oscillator (the "engine block") — its pitch tracks
 *     the player's speed, so revving up rises in tone.
 *   • A sine harmonic +12 semitones above base, faded in only when nitro
 *     is active — that's the "vroom" overlay.
 *
 * AudioContext is created lazily on first user gesture (Start tap) so iOS
 * Safari allows the sound. Master gain capped at 0.30 per the brief.
 */

export type EngineAudio = {
  setSpeed(speedKmh: number): void;
  setNitro(on: boolean): void;
  start(): void;
  stop(): void;
  mute(on: boolean): void;
};

const MASTER_GAIN = 0.30;

export function createEngineAudio(): EngineAudio {
  type Ctx = AudioContext | null;
  let ctx: Ctx = null;
  let master: GainNode | null = null;
  let base: OscillatorNode | null = null;
  let nitroOsc: OscillatorNode | null = null;
  let nitroGain: GainNode | null = null;
  let started = false;
  let muted = false;
  let currentSpeed = 0;

  function ensure(): boolean {
    if (ctx) return true;
    try {
      const AC = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
      if (!AC) return false;
      ctx = new AC();
      master = ctx.createGain();
      master.gain.value = muted ? 0 : MASTER_GAIN;
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 900;
      lp.Q.value = 0.6;

      base = ctx.createOscillator();
      base.type = "sawtooth";
      base.frequency.value = 80;

      nitroOsc = ctx.createOscillator();
      nitroOsc.type = "sine";
      nitroOsc.frequency.value = 160;          // one octave above base
      nitroGain = ctx.createGain();
      nitroGain.gain.value = 0;                // fade in only when nitro fires

      base.connect(lp).connect(master).connect(ctx.destination);
      nitroOsc.connect(nitroGain).connect(master);
      return true;
    } catch {
      return false;
    }
  }

  function start() {
    if (started) return;
    if (!ensure()) return;
    if (ctx!.state === "suspended") ctx!.resume().catch(() => { /* ok */ });
    try { base!.start(); } catch { /* already started */ }
    try { nitroOsc!.start(); } catch { /* already started */ }
    started = true;
  }

  function stop() {
    if (!ctx) return;
    try { base?.stop(); } catch { /* not started */ }
    try { nitroOsc?.stop(); } catch { /* not started */ }
    started = false;
  }

  function setSpeed(speedKmh: number) {
    currentSpeed = speedKmh;
    if (!ctx || !base) return;
    // 60Hz idle → ~220Hz top end. Smooth ramp so it sweeps instead of pops.
    const target = 60 + Math.max(0, speedKmh) * 0.5;
    const now = ctx.currentTime;
    base.frequency.cancelScheduledValues(now);
    base.frequency.linearRampToValueAtTime(target, now + 0.12);
    if (nitroOsc) {
      const harmonic = target * 2;
      nitroOsc.frequency.cancelScheduledValues(now);
      nitroOsc.frequency.linearRampToValueAtTime(harmonic, now + 0.12);
    }
  }

  function setNitro(on: boolean) {
    if (!ctx || !nitroGain) return;
    const now = ctx.currentTime;
    nitroGain.gain.cancelScheduledValues(now);
    nitroGain.gain.linearRampToValueAtTime(on ? 0.22 : 0, now + 0.08);
  }

  function mute(on: boolean) {
    muted = on;
    if (!ctx || !master) return;
    const now = ctx.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.linearRampToValueAtTime(on ? 0 : MASTER_GAIN, now + 0.05);
  }

  return { setSpeed, setNitro, start, stop, mute };
}
